import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AssetTypes, CollectionFormType, CollectionType, ImageType, Pageable, StoryType } from '../../../lib/types'
import Player from '../../../components/Player'
import Cover from '../../../components/Cover'
import Box from '@mui/material/Box'
import Content from './Content'
import TabPanel from '@mui/lab/TabPanel'
import Stories from './Stories'
import Photos from './Photos'
import Links from './Links'
import Files from './Files'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import Tab from '@mui/material/Tab'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import QueryString, { parse, stringify } from 'qs'
import { UserContext } from '../../../components/UserProvider'
import { SimpleSnackbarContext } from '../../../components/SimpleSnackbar'
import api from '../../../lib/api'
import MetaTags from 'react-meta-tags'
import CircularLoadingCentred from '../../../components/CircularLoadingCentred'
import Comments from '../../../components/Comments'
import Paper from '@mui/material/Paper'
import EditBar from './EditBar'
import { usePermissions } from '../../../components/hooks/usePermissions'
import { StoryFilters } from './storyFilters'

const STORY_PAGE_SIZE = 100

const DEFAULT_STORY_FILTERS: StoryFilters = {
  sortBy: 'createdAt',
  sortDirection: 'asc'
}

const parseStoryFiltersFromSearch = (search: string): StoryFilters => {
  const parsed = parse(search, { ignoreQueryPrefix: true })
  return {
    ...DEFAULT_STORY_FILTERS,
    recorderName:
      typeof parsed.recorderName === 'string' && parsed.recorderName.trim() ? parsed.recorderName.trim() : undefined,
    q: typeof parsed.q === 'string' && parsed.q.trim() ? parsed.q.trim() : undefined,
    sortBy: parsed.sortBy === 'title' ? 'title' : 'createdAt',
    sortDirection: parsed.sortDirection === 'desc' ? 'desc' : 'asc'
  }
}

const isPlayableStory = (story: StoryType) => !!(story.ready && story.url)

const Collection: React.FC<
  RouteComponentProps<{
    collectionId: string
  }>
> = ({ match, history, location }) => {
  const [playlist, setPlaylist] = useState<any[] | undefined>([])
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const { user } = useContext(UserContext)
  const { collectionId } = match.params
  const { autoplay, storyId } = parse(location?.search as string, { ignoreQueryPrefix: true })
  const [collection, setCollection] = useState<CollectionType | null>(null)
  const [story, setStory] = useState<StoryType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [play, setPlay] = useState<boolean>(!!autoplay)
  const [tab, setTab] = useState<string>('')
  const [isOwner, setIsOwner] = useState<boolean>(false)
  const [isEditor, setIsEditor] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [showShare, setShowShare] = useState<boolean>(false)
  const [item, setItem] = useState<any>({})
  const [storyFilters, setStoryFilters] = useState<StoryFilters>(() => parseStoryFiltersFromSearch(location.search))
  const [recorders, setRecorders] = useState<string[]>([])
  const [storiesTotal, setStoriesTotal] = useState<number>(0)
  const [storiesPage, setStoriesPage] = useState<number>(0)
  const [storiesHasNextPage, setStoriesHasNextPage] = useState<boolean>(false)
  const [storiesLoadingMore, setStoriesLoadingMore] = useState<boolean>(false)
  const [storiesFiltering, setStoriesFiltering] = useState<boolean>(false)
  const skipFilterRefetchRef = useRef(true)
  const { isEditor: isTeamMemberEditor } = usePermissions()

  const shiftUntilStoryId = (
    arr: StoryType[],
    id?: string | QueryString.ParsedQs | string[] | QueryString.ParsedQs[] | undefined
  ): StoryType[] => {
    if (!id || arr.length === 0) return arr
    const story = arr.find(st => st.id === id)
    if (!story) return arr

    const index = arr.indexOf(story)
    return [...arr.slice(index), ...arr.slice(0, index)]
  }

  useEffect(() => {
    setPlay(!!autoplay)
  }, [autoplay])

  const computeCollection = useCallback(
    (value: CollectionType) => {
      setCollection(value)

      try {
        let playlist: any[] = []
        let stories = value?.stories?.filter(isPlayableStory) || []
        stories = shiftUntilStoryId(stories, storyId)
        if (stories) {
          playlist = stories.map(story => ({
            sources: [
              {
                src: story.url,
                type: story.mimeType
              }
            ],
            thumb: story.thumbnailUrl,
            title: `${story.title}`,
            duration: story.duration,
            metaDescription: `${story.description}`,
            tracks:
              story.subtitles?.map(subtitle => ({
                kind: 'captions',
                src: subtitle.url,
                srclang: subtitle.language,
                label: subtitle.language,
                default: '1'
              })) || []
          }))
        }
        setPlaylist(playlist)
      } catch (e) {
        history.push('/500')
      } finally {
        setIsLoading(false)
      }
    },
    [history, storyId]
  )

  const computeCollectionRef = useRef(computeCollection)
  computeCollectionRef.current = computeCollection

  const updateStoriesListOnly = useCallback((storiesPage: Pageable<StoryType>, append = false) => {
    setStoriesTotal(storiesPage.totalElements)
    setStoriesPage(storiesPage.number)
    setStoriesHasNextPage(!storiesPage.last)
    setCollection(prev => {
      if (!prev) return prev
      const stories = append ? [...(prev.stories || []), ...(storiesPage.content || [])] : storiesPage.content || []
      return { ...prev, stories }
    })
  }, [])

  const loadStoriesPage = useCallback(
    async (page: number) =>
      api.getCollectionStories(collectionId, page, STORY_PAGE_SIZE, {
        readyOnly: false,
        recorderName: storyFilters.recorderName,
        q: storyFilters.q,
        sortBy: storyFilters.sortBy,
        sortDirection: storyFilters.sortDirection
      }),
    [collectionId, storyFilters]
  )

  const applyStoriesPage = useCallback(
    (summary: CollectionType, storiesPage: Pageable<StoryType>) => {
      setStoriesTotal(storiesPage.totalElements)
      setStoriesPage(storiesPage.number)
      setStoriesHasNextPage(!storiesPage.last)
      computeCollection({
        ...summary,
        stories: storiesPage.content || []
      })
    },
    [computeCollection]
  )

  const fetch = useCallback(async () => {
    const [summary, storiesPage, recordersList] = await Promise.all([
      api.getCollectionSummary(collectionId),
      loadStoriesPage(0),
      api.getCollectionStoryRecorders(collectionId, false)
    ])
    setRecorders(recordersList)
    applyStoriesPage(summary, storiesPage)
  }, [collectionId, loadStoriesPage, applyStoriesPage])

  const refetchStoriesForFilters = useCallback(async () => {
    setStoriesFiltering(true)
    try {
      const storiesPage = await loadStoriesPage(0)
      updateStoriesListOnly(storiesPage, false)
    } finally {
      setStoriesFiltering(false)
    }
  }, [loadStoriesPage, updateStoriesListOnly])

  const loadMoreStories = useCallback(async () => {
    if (!collection || storiesLoadingMore || !storiesHasNextPage) return
    setStoriesLoadingMore(true)
    try {
      const nextPage = storiesPage + 1
      const storiesPageResult = await loadStoriesPage(nextPage)
      updateStoriesListOnly(storiesPageResult, true)
    } catch {
      snackActions.open('Failed to load more stories. Please try again.')
    } finally {
      setStoriesLoadingMore(false)
    }
  }, [
    collection,
    loadStoriesPage,
    storiesHasNextPage,
    storiesLoadingMore,
    storiesPage,
    updateStoriesListOnly,
    snackActions
  ])

  const updateCollection = async (formData: CollectionFormType) => {
    try {
      const result = await api.updateCollection(collectionId, formData)
      computeCollection(result)
    } catch (error) {
      // @ts-ignore
      const { data } = error.response
      if (data.code === '1018') {
        snackActions.open('This collection cannot be edited!')
        throw error
      }
      snackActions.open('Something was wrong! please try again.')
      throw error
    }
  }

  const handleStoryFiltersChange = useCallback(
    (next: StoryFilters) => {
      setStoryFilters(current => {
        if (
          current.recorderName === next.recorderName &&
          current.q === next.q &&
          current.sortBy === next.sortBy &&
          current.sortDirection === next.sortDirection
        ) {
          return current
        }

        const searchParams = parse(location.search, { ignoreQueryPrefix: true }) as Record<string, unknown>
        const updated: Record<string, unknown> = { ...searchParams }
        if (next.recorderName) {
          updated.recorderName = next.recorderName
        } else {
          delete updated.recorderName
        }
        if (next.q) {
          updated.q = next.q
        } else {
          delete updated.q
        }
        history.replace({
          pathname: location.pathname,
          search: stringify(updated, { addQueryPrefix: true, skipNulls: true })
        })

        return next
      })
    },
    [history, location.pathname, location.search]
  )

  useEffect(() => {
    skipFilterRefetchRef.current = true
    const initialFilters = parseStoryFiltersFromSearch(location.search)
    setStoryFilters(initialFilters)

    let cancelled = false
    setIsLoading(true)
    ;(async () => {
      try {
        const [summary, storiesPage, recordersList] = await Promise.all([
          api.getCollectionSummary(collectionId),
          api.getCollectionStories(collectionId, 0, STORY_PAGE_SIZE, {
            readyOnly: false,
            recorderName: initialFilters.recorderName,
            q: initialFilters.q,
            sortBy: initialFilters.sortBy,
            sortDirection: initialFilters.sortDirection
          }),
          api.getCollectionStoryRecorders(collectionId, false)
        ])
        if (cancelled) return
        setRecorders(recordersList)
        setStoriesTotal(storiesPage.totalElements)
        setStoriesPage(storiesPage.number)
        setStoriesHasNextPage(!storiesPage.last)
        computeCollectionRef.current({
          ...summary,
          stories: storiesPage.content || []
        })
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [collectionId])

  useEffect(() => {
    if (skipFilterRefetchRef.current) {
      skipFilterRefetchRef.current = false
      return
    }
    refetchStoriesForFilters()
  }, [storyFilters, refetchStoriesForFilters])

  useEffect(() => {
    if (collection?.owner.id === user?.id || isTeamMemberEditor) {
      setIsOwner(true)
    }
    if (collection?.watchers?.find(e => user?.email === e.email && e.type === 'EDITOR')) {
      setIsEditor(true)
    }
  }, [user, collection])

  useEffect(() => {
    if (!collection?.id) return
    const playableStories = collection.stories.filter(isPlayableStory)
    setStory(playableStories.find(story => story.id === storyId) || playableStories[0] || null)
  }, [storyId, collection])

  if (isLoading) {
    return <CircularLoadingCentred />
  }

  if (!collection) {
    return <Redirect to={'/a/404'} />
  }

  const { thumbnailUrl, title, description, stories, owner, type } = collection

  const handlePlay = () => {
    if (!story?.id) {
      snackActions.open('There are no ready stories available to play in this collection.')
      return
    }
    setPlay(true)
  }

  const onTabClick = (tab = '') => {
    setTab(tab)
  }

  const updateCover = async (image: ImageType) => {
    try {
      await api.updateCollectionCover(collectionId, image.id)
      fetch()
    } catch (error) {
      // @ts-ignore
      const { data } = error.response
      if (data.code === '1018') {
        snackActions.open('This collection cannot be edited!')
        throw error
      }
      snackActions.open('Something was wrong! please try again.')
      throw error
    }
  }

  return (
    <>
      <MetaTags>
        <title>Rakonto - {title}</title>
        <meta property="description" content={description || ''} />
        <meta property="creator" content={owner.firstName || ''} />
        <meta property="publisher" content={'Rakonto'} />
        <meta property="og:image" content={thumbnailUrl} />
      </MetaTags>
      <Box
        sx={{
          width: '100%',
          height: `100%`,
          display: 'flex',
          flexFlow: 'column'
        }}
      >
        <Box sx={{ width: '100%', height: 'auto' }}>
          {play ? (
            <Player
              mobileMaxHeight={'50svh'}
              key={story?.id}
              subtitles={story?.subtitles || []}
              type={story?.type}
              media={story?.video || story?.audio}
              cover={story?.thumbnailUrl}
              autoplay={!!autoplay}
              playlist={playlist || []}
              onLoadedMetaData={setItem}
            />
          ) : (
            <Cover
              author={collection.owner}
              subtitle={collection.type}
              src={thumbnailUrl}
              title={title}
              description={description}
              onClick={handlePlay}
              hidePlayButton={!story?.id}
              buttonLabel="View stories"
              date={collection.createdAt}
            />
          )}
        </Box>
        <Box
          component={TabContext}
          value={(tab as string) || 'content'}
          sx={{
            width: '100%',
            height: '100%'
          }}
        >
          <Box
            component={TabList}
            variant="scrollable"
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 6
            }}
          >
            <Tab label="Content" value="content" onClick={() => onTabClick('content')} />
            <Tab label="Comments" value="comments" onClick={() => onTabClick('comments')} />
            {/* <Tab label="People" value="people" onClick={() => onTabClick('people')} />
            <Tab label="Places" value="places" onClick={() => onTabClick('places')} />
            <Tab label="Timelines" value="timelines" onClick={() => onTabClick('timelines')} /> */}
            <Tab label="Photos" value="photos" onClick={() => onTabClick('photos')} />
            <Tab label="Files" value="files" onClick={() => onTabClick('files')} />
            <Tab label="Links" value="links" onClick={() => onTabClick('links')} />
          </Box>
          <Box
            sx={{
              width: '100%',
              padding: 3
            }}
          >
            <EditBar
              id={collectionId}
              refetch={fetch}
              onChange={updateCover}
              canEdit={isOwner}
              collection={collection}
              setEditMode={setEditMode}
              showShare={showShare}
              setShowShare={setShowShare}
            />
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="content">
              <Content
                type={type}
                update={updateCollection}
                canEdit={isOwner}
                title={title}
                id={collectionId}
                description={description}
                isPlaying={play}
                editMode={editMode}
                setEditMode={setEditMode}
                themeId={collection.theme?.id}
              >
                <Stories
                  reload={fetch}
                  collectionId={collectionId}
                  selectedStory={collection?.stories?.find(st => st?.title === item?.metatitle)?.id ?? story?.id}
                  playing={story?.title === item?.metatitle}
                  stories={stories}
                  recorders={recorders}
                  totalStories={storiesTotal}
                  storyFilters={storyFilters}
                  onFiltersChange={handleStoryFiltersChange}
                  filteringStories={storiesFiltering}
                  hasMoreStories={storiesHasNextPage}
                  loadingMoreStories={storiesLoadingMore}
                  onLoadMoreStories={loadMoreStories}
                  canEdit={isOwner}
                  stats={collection.stats}
                />
              </Content>
            </TabPanel>
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="comments">
              <Box component={Paper} sx={{ marginTop: 3 }}>
                <Comments type={AssetTypes.collection} id={collectionId} watchers={[]} />
              </Box>
            </TabPanel>
            {/* <TabPanel sx={{ height: '100%', padding: 'unset' }} value="people">
              <People list={collection.stories} />
            </TabPanel>
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="timelines">
              <Timelines list={collection.stories} />
            </TabPanel>
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="places">
              <Places list={stories} />
            </TabPanel> */}
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="photos">
              <Photos list={stories} isEditor={isOwner || isEditor} />
            </TabPanel>
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="files">
              <Files list={stories} isEditor={isOwner || isEditor} />
            </TabPanel>
            <TabPanel sx={{ height: '100%', padding: 'unset' }} value="links">
              <Links list={stories} />
            </TabPanel>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default Collection
